import React from 'react'
import TextField from '@mui/material/TextField'
import { FieldValues } from '../IMSFormFieldsTypes'
import Stack from '@mui/material/Stack'
import { IMS_FORM_IMG_PLACEHOLDER } from '../../../../../Constants'
import { makeStyles } from '@material-ui/styles'

const useStyles = makeStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '80%',
        justifyContent: 'flex-start',
    },
    prefix: {
        marginRight: '5px',
    },
    suffix: {
        marginLeft: '5px',
    },
    textField: {
        width: '300px',
    },
    img: {
        width: 56,
        height: 56,
        cursor: 'pointer',
    },
})

interface Component {
    id: React.Key | null | undefined
    datatype: string
    label_display: any
    label: any
    prefix?:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactPortal
        | null
        | undefined
    suffix?:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactPortal
        | null
        | undefined
    component_response?: { value: any; photo?: any }
    photo_capture?: number
}

interface Group {
    id: React.Key | null | undefined
}

interface IMSFormAccordionTextFieldProps {
    component: Component
    group: Group
    fieldValues: FieldValues
    handleFieldChange: (
        groupId: string,
        componentId: string,
        value: any,
        datatype: string
    ) => void
    setLightboxImage: (image: string | null) => void
    setLightboxOpen: (open: boolean) => void
}

const IMSFormAccordionTextField: React.FC<IMSFormAccordionTextFieldProps> = ({
    component,
    group,
    fieldValues,
    handleFieldChange,
    setLightboxImage,
    setLightboxOpen,
}) => {
    const classes = useStyles()

    const componentIdStr = component.id != null ? String(component.id) : ''
    const groupIdStr = group.id != null ? String(group.id) : ''

    // Robust value extraction logic
    let currentValue: string = ''
    const field = fieldValues[componentIdStr]
    if (
        field &&
        typeof field === 'object' &&
        field !== null &&
        'value' in field
    ) {
        currentValue =
            field.value !== undefined && field.value !== null
                ? String(field.value)
                : ''
    } else if (typeof field === 'string' || typeof field === 'number') {
        currentValue = String(field)
    } else if (
        component.component_response &&
        typeof component.component_response === 'object' &&
        component.component_response !== null &&
        'value' in component.component_response
    ) {
        currentValue =
            component.component_response.value !== undefined &&
            component.component_response.value !== null
                ? String(component.component_response.value)
                : ''
    } else if (
        typeof component.component_response === 'string' ||
        typeof component.component_response === 'number'
    ) {
        currentValue = String(component.component_response)
    }

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (groupIdStr && componentIdStr) {
            const raw = event.target.value
            const cleaned =
                component.datatype === 'number' ? raw.replace(/\D+/g, '') : raw
            handleFieldChange(
                groupIdStr,
                componentIdStr,
                cleaned,
                component.datatype
            )
        }
    }

    const photoUrl =
        typeof component.component_response?.photo === 'string'
            ? component.component_response.photo
            : null
    const displayPhoto = component.photo_capture === 1 && photoUrl

    return (
        <div
            className={classes.container}
            style={{
                justifyContent: displayPhoto ? 'space-between' : 'flex-start',
            }}
        >
            {component.prefix && (
                <span className={classes.prefix}>{component.prefix}</span>
            )}
            <TextField
                type={component.datatype === 'number' ? 'text' : 'text'}
                inputMode={
                    component.datatype === 'number' ? 'numeric' : undefined
                }
                id={componentIdStr || undefined}
                variant="standard"
                className={classes.textField}
                label={component.label_display || component.label}
                value={currentValue}
                onChange={handleChange}
            />
            {component.suffix && (
                <span className={classes.suffix}>{component.suffix}</span>
            )}
            {displayPhoto && (
                <Stack direction="row" spacing={2}>
                    <img
                        src={photoUrl || IMS_FORM_IMG_PLACEHOLDER}
                        alt="Thumbnail"
                        className={classes.img}
                        onClick={() => {
                            setLightboxImage(photoUrl)
                            setLightboxOpen(true)
                        }}
                    />
                </Stack>
            )}
        </div>
    )
}

export default IMSFormAccordionTextField
